import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import { useNotificationStore } from '@/stores/notification';
import router from './router';
import vuetify from './plugins/vuetify';
import '@/scss/style.scss';
// Import global table styles
import '@/assets/scss/tables.scss';
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
import apexChartsPlugin from './plugins/apexcharts';
import VueTablerIcons from 'vue-tabler-icons';

import { vMaska } from 'maska';
// print
import print from 'vue3-print-nb';
//i18
import { createI18n } from 'vue-i18n';
import messages from '@/utils/locales/messages';

// Import DatePicker styles
import '@vuepic/vue-datepicker/dist/main.css';
import Datepicker from '@vuepic/vue-datepicker';

// Disable Pinia console logs
// This needs to happen before any imports that might use Pinia
const originalConsoleLog = console.log;
console.log = (...args) => {
  // Filter out Pinia logs that include the 🍍 emoji
  if (typeof args[0] === 'string' && args[0].includes('🍍')) {
    return;
  }

};

const i18n = createI18n({
  locale: 'en',
  messages: messages,
  silentTranslationWarn: true,
  silentFallbackWarn: true
});

const pinia = createPinia();

const app = createApp(App);

// Disable warnings in production
if (import.meta.env.PROD) {
  // Disable Vue warnings
  app.config.warnHandler = () => null;
  
  // Disable console warnings
  const originalConsoleWarn = console.warn;
  console.warn = (...args) => {
    // Filter out passive event listener and Suspense warnings
    if (
      typeof args[0] === 'string' &&
      (args[0].includes('passive event') ||
       args[0].includes('Suspense'))
    ) {
      return;
    }
    originalConsoleWarn(...args);
  };
}

app.use(router);
app.use(PerfectScrollbarPlugin);
app.use(pinia);
app.use(VueTablerIcons);
app.use(print);
app.use(i18n);
app.directive('maska', vMaska);
app.use(apexChartsPlugin);
app.use(vuetify);

// Register Datepicker globally
app.component('Datepicker', Datepicker);

// App-wide error safety net.
// Pinia is already registered above, so the notification store is safe to use here.
const notifyOfUncaughtError = (): void => {
  try {
    useNotificationStore().showError({
      title: 'Something went wrong',
      text: 'An unexpected error occurred. Please reload the page if the problem persists.'
    });
  } catch {
    // Never let the error handler itself throw.
  }
};

// Catch uncaught errors thrown from inside Vue components.
app.config.errorHandler = (err, _instance, info) => {
  console.error('[Vue errorHandler]', err, info);
  notifyOfUncaughtError();
};

// Catch unhandled promise rejections (e.g. failed async calls with no .catch).
window.addEventListener('unhandledrejection', (event) => {
  console.error('[unhandledrejection]', event.reason);
  notifyOfUncaughtError();
});

app.mount('#app');
